import React, { useState, useEffect, useCallback } from 'react';
import ConductorCatalog from './ConductorCatalog';
import {
  loadLineDefinitions,
  saveLineDefinition,
  createLineDefinition,
  deleteLineDefinition,
  calculateLineLoss,
  lineToPyPSA,
} from '../store/lineDefinitions';

const FIELD_TYPES = ['text', 'number', 'select', 'boolean'];

const LINE_CATEGORIES = [
  {
    id: 'electric',
    label: 'Electric Lines',
    color: '#D4A843',
    borderColor: '#B8922A',
    icon: '\u26A1',
    description: 'Overhead conductors, underground cables, busbars between substations.',
    lineTypes: ['overhead', 'underground'],
  },
  {
    id: 'gas',
    label: 'Gas Lines / Pipelines',
    color: '#3CC0C9',
    borderColor: '#2A9DA5',
    icon: '\uD83D\uDD25',
    description: 'Transmission and distribution gas lines and pipelines, compressor connections.',
    lineTypes: ['transmission', 'distribution'],
  },
  {
    id: 'liquid',
    label: 'Liquid Lines / Pipelines',
    color: '#7B68AE',
    borderColor: '#5F4F8B',
    icon: '\uD83D\uDCA7',
    description: 'Process piping, water mains, chemical transfer lines and pipelines.',
    lineTypes: ['process', 'transfer', 'utility'],
  },
];

/* Mini loss curve for electric lines */
const LineLossCurve = ({ def }) => {
  const w = 200, h = 60;
  const pad = { t: 8, r: 8, b: 16, l: 30 };
  const plotW = w - pad.l - pad.r, plotH = h - pad.t - pad.b;
  const len = 10; // reference 10km
  const points = [];
  let maxLoss = 0;
  for (let pct = 0; pct <= 120; pct += 5) {
    const result = calculateLineLoss(def, pct / 100, len);
    maxLoss = Math.max(maxLoss, result.loss_pct);
    points.push({ x: pct, y: result.loss_pct });
  }
  maxLoss = Math.max(maxLoss, 0.01);
  const pathD = points.map((p, i) => {
    const x = pad.l + (p.x / 120) * plotW;
    const y = pad.t + plotH - (p.y / maxLoss) * plotH;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <svg width={w} height={h} className="loss-curve">
      <text x={pad.l - 4} y={pad.t + 4} textAnchor="end" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">{maxLoss.toFixed(1)}%</text>
      <text x={pad.l} y={h - 2} fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">0%</text>
      <text x={w - pad.r} y={h - 2} textAnchor="end" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">120%</text>
      <path d={pathD} fill="none" stroke={def.color || '#4ecca3'} strokeWidth={1.5} />
    </svg>
  );
};

/* Line icon preview */
const LinePreview = ({ def, catColor }) => {
  const isDashed = def.lineStyle === 'dashed' || def.lineType === 'underground';
  const color = def.color || catColor || '#666';
  return (
    <div className="line-preview-box">
      <svg width={80} height={40}>
        <circle cx={10} cy={20} r={6} fill={color} opacity={0.3} stroke={color} strokeWidth={1.5} />
        <line x1={16} y1={20} x2={64} y2={20} stroke={color} strokeWidth={isDashed ? 2 : 3} strokeDasharray={isDashed ? '6 4' : 'none'} />
        <circle cx={70} cy={20} r={6} fill={color} opacity={0.3} stroke={color} strokeWidth={1.5} />
      </svg>
      <span className="line-preview-type">{def.lineType || 'line'}</span>
    </div>
  );
};

const LineDefinitionsView = () => {
  const [allDefs, setAllDefs] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [editDef, setEditDef] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showPyPSA, setShowPyPSA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCatalog, setShowCatalog] = useState(null); // null | 'browse' | 'select'

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setAllDefs(await loadLineDefinitions()); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const defsByCategory = {};
  for (const cat of LINE_CATEGORIES) {
    defsByCategory[cat.id] = allDefs.filter(d => d.category === cat.id);
  }

  // Create a new line definition from a conductor catalog entry
  const handleCreateFromConductor = (conductor) => {
    const voltClass = conductor.voltage_class || '';
    const voltKv = (conductor.voltage_levels && conductor.voltage_levels[0]) || 0;
    const codeName = conductor.code_name || '';
    const idBase = (codeName || conductor.material + '_' + conductor.cross_section_mm2).toLowerCase().replace(/\s+/g, '_');
    const lineId = `${idBase}_${Math.round(voltKv)}kv`;

    const newDef = {
      id: lineId,
      category: 'electric',
      name: conductor.name,
      lineType: conductor.type?.includes('underground') ? 'underground' : 'overhead',
      voltage_kv: voltKv,
      conductor: conductor.name,
      conductor_id: conductor.id,
      code_name: codeName,
      cross_section_mm2: conductor.cross_section_mm2,
      material: conductor.material,
      r_ohm_per_km: conductor.ac_resistance_75c_ohm_per_km || conductor.dc_resistance_20c_ohm_per_km,
      x_ohm_per_km: conductor.reactance_ohm_per_km,
      b_us_per_km: conductor.susceptance_us_per_km,
      i_thermal_a: conductor.thermal_rating_a,
      i_emergency_a: conductor.emergency_rating_a,
      s_max_mva: null, // calculated per voltage: sqrt(3) * V * I / 1e6
      // Loss at rated load for 1km: 3 × I² × R / (√3 × V × I × 1000) × 100 = √3 × I × R / (V × 10)
      loss_pct_per_km: (conductor.thermal_rating_a && conductor.ac_resistance_75c_ohm_per_km && voltKv)
        ? parseFloat((Math.sqrt(3) * conductor.thermal_rating_a * conductor.ac_resistance_75c_ohm_per_km / (voltKv * 10)).toFixed(3))
        : null,
      dc_resistance_20c: conductor.dc_resistance_20c_ohm_per_km,
      ac_resistance_50c: conductor.ac_resistance_50c_ohm_per_km,
      ac_resistance_75c: conductor.ac_resistance_75c_ohm_per_km,
      ac_resistance_90c: conductor.ac_resistance_90c_ohm_per_km,
      max_temp_c: conductor.max_operating_temp_c,
      diameter_mm: conductor.overall_diameter_mm,
      weight_kg_per_km: conductor.weight_kg_per_km,
      breaking_load_kn: conductor.breaking_load_kn,
      stranding: conductor.stranding,
      insulation: conductor.insulation,
      standard: conductor.standard,
      color: voltKv >= 132 ? '#B88A14' : voltKv >= 33 ? '#D4A020' : voltKv >= 11 ? '#E8B828' : '#F5C542',
      lineStyle: conductor.type?.includes('underground') ? 'dashed' : 'solid',
      lineWidth: voltKv >= 400 ? 5 : voltKv >= 132 ? 4 : voltKv >= 33 ? 3 : voltKv >= 11 ? 2 : 1,
      fields: [
        { id: 'f1', name: 'length_km', label: 'Length', type: 'number', defaultValue: '', unit: 'km', required: true },
        { id: 'f2', name: 'circuits', label: 'Circuits', type: 'select', options: ['Single', 'Double', 'Bundle'], defaultValue: 'Single', required: false },
        { id: 'f3', name: 'ambient_temp_c', label: 'Ambient Temp', type: 'number', defaultValue: '40', unit: '\u00B0C', required: false },
        { id: 'f4', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'planned', required: true },
      ],
    };
    // Calculate S_max: sqrt(3) * V_kV * I_A / 1000
    if (voltKv && conductor.thermal_rating_a) {
      newDef.s_max_mva = parseFloat((Math.sqrt(3) * voltKv * conductor.thermal_rating_a / 1000).toFixed(1));
    }

    setShowCatalog(null);
    setEditDef(newDef);
    setDirty(true); // it's a new unsaved definition
  };

  // Editor helpers
  const startEdit = (def) => { setEditDef(JSON.parse(JSON.stringify(def))); setDirty(false); };
  const updateEdit = (key, value) => { setEditDef(prev => ({ ...prev, [key]: value })); setDirty(true); };
  const handleSave = async () => {
    if (!editDef) return;
    try {
      const existing = allDefs.find(d => d.id === editDef.id);
      let ok;
      if (existing) {
        ok = await saveLineDefinition(editDef);
      } else {
        ok = await createLineDefinition(editDef);
      }
      if (ok) {
        await refresh();
        setDirty(false);
        // After creating, go back to list
        if (!existing) setEditDef(null);
      } else {
        alert('Failed to save. Check the ID is unique.');
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
  };
  const handleDelete = async (id) => { await deleteLineDefinition(id); await refresh(); };

  const addField = () => {
    const id = 'f' + Date.now();
    setEditDef(prev => ({ ...prev, fields: [...(prev.fields || []), { id, name: '', label: '', type: 'text', defaultValue: '', required: false }] }));
    setDirty(true);
  };
  const updateField = (fid, key, val) => { setEditDef(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fid ? { ...f, [key]: val } : f) })); setDirty(true); };
  const removeField = (fid) => { setEditDef(prev => ({ ...prev, fields: prev.fields.filter(f => f.id !== fid) })); setDirty(true); };

  // ========== CONDUCTOR CATALOG ==========
  if (showCatalog) {
    return (
      <ConductorCatalog
        onBack={() => setShowCatalog(null)}
        selectionMode={showCatalog === 'select'}
        onSelect={showCatalog === 'select' ? handleCreateFromConductor : undefined}
      />
    );
  }

  // ========== EDITOR ==========
  if (editDef) {
    const catInfo = LINE_CATEGORIES.find(c => c.id === editDef.category) || LINE_CATEGORIES[0];
    const isElectric = editDef.category === 'electric';
    const isNew = !allDefs.find(d => d.id === editDef.id);
    const pypsa = isElectric ? lineToPyPSA(editDef) : null;

    return (
      <div className="def-editor">
        <div className="def-editor-header">
          <div className="def-editor-title">
            <LinePreview def={editDef} catColor={catInfo.color} />
            <div>
              <h2>{editDef.name}</h2>
              <span className="def-editor-id">{editDef.id} &middot; {editDef.lineType} &middot; {isElectric ? `${editDef.voltage_kv} kV` : catInfo.label}</span>
            </div>
          </div>
          <div className="def-editor-actions">
            <button className="btn-ghost" onClick={() => { setEditDef(null); setDirty(false); }}>Back</button>
            <button className="btn-accent" onClick={handleSave} disabled={!dirty}>
              {!dirty ? 'Saved' : isNew ? 'Add to Portfolio' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="def-editor-body">
          <div className="def-editor-left">
            {/* Properties */}
            <div className="def-section">
              <h3>{isNew ? 'New Line from Conductor Catalog' : 'Line Properties'}</h3>
              <div className="def-form-row">
                <div className="def-form-group"><label>Name</label><input value={editDef.name} onChange={e => updateEdit('name', e.target.value)} /></div>
                <div className="def-form-group" style={{ width: 160 }}>
                  <label>ID</label>
                  <input value={editDef.id} onChange={e => updateEdit('id', e.target.value.toLowerCase().replace(/\s+/g, '_'))} disabled={!isNew} className="mono" />
                </div>
                <div className="def-form-group" style={{ width: 120 }}>
                  <label>Type</label>
                  <select value={editDef.lineType || ''} onChange={e => updateEdit('lineType', e.target.value)}>
                    {catInfo.lineTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {editDef.conductor && (
                <div className="ref-info-box" style={{ marginTop: 8 }}>
                  <div className="ref-info-header"><span>Source Conductor</span></div>
                  <div className="ref-info-values">
                    <span>{editDef.conductor}</span>
                    <span>{editDef.material} {editDef.cross_section_mm2}mm{'\u00B2'}</span>
                    {editDef.stranding && <span>{editDef.stranding}</span>}
                    {editDef.standard && <span>{editDef.standard}</span>}
                  </div>
                </div>
              )}

              {isElectric && (
                <>
                  <div className="def-form-row">
                    <div className="def-form-group"><label>Conductor</label><input value={editDef.conductor || ''} onChange={e => updateEdit('conductor', e.target.value)} /></div>
                    <div className="def-form-group" style={{ width: 100 }}><label>Voltage (kV)</label><input type="number" value={editDef.voltage_kv || ''} onChange={e => updateEdit('voltage_kv', parseFloat(e.target.value) || 0)} /></div>
                    <div className="def-form-group" style={{ width: 120 }}><label>Cross Section</label><input type="number" value={editDef.cross_section_mm2 || ''} onChange={e => updateEdit('cross_section_mm2', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">mm²</span></div>
                  </div>
                  <div className="def-form-row">
                    <div className="def-form-group"><label>R</label><input type="number" step="0.0001" value={editDef.r_ohm_per_km || ''} onChange={e => updateEdit('r_ohm_per_km', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">{'Ω/km'}</span></div>
                    <div className="def-form-group"><label>X</label><input type="number" step="0.001" value={editDef.x_ohm_per_km || ''} onChange={e => updateEdit('x_ohm_per_km', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">{'Ω/km'}</span></div>
                    <div className="def-form-group"><label>B</label><input type="number" step="0.01" value={editDef.b_us_per_km || ''} onChange={e => updateEdit('b_us_per_km', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">{'μS/km'}</span></div>
                  </div>
                  <div className="def-form-row">
                    <div className="def-form-group"><label>I thermal</label><input type="number" value={editDef.i_thermal_a || ''} onChange={e => updateEdit('i_thermal_a', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">A</span></div>
                    <div className="def-form-group"><label>S max</label><input type="number" step="0.1" value={editDef.s_max_mva || ''} onChange={e => updateEdit('s_max_mva', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">MVA</span></div>
                    <div className="def-form-group"><label>Loss</label><input type="number" step="0.01" value={editDef.loss_pct_per_km || ''} onChange={e => updateEdit('loss_pct_per_km', parseFloat(e.target.value) || 0)} /><span className="field-unit-inline">%/km</span></div>
                  </div>

                  {/* Loss curve */}
                  <div className="loss-formula-box">
                    <div className="loss-formula">Loss% = {editDef.loss_pct_per_km || 0}%/km {'×'} L {'×'} (I/I_rated){'²'}</div>
                    <LineLossCurve def={editDef} />
                  </div>

                  {/* PyPSA */}
                  {pypsa && (
                    <div className="pypsa-box">
                      <h3>PyPSA Parameters</h3>
                      <div className="pypsa-params">
                        {Object.entries(pypsa).map(([k, v]) => (
                          <div key={k} className="pypsa-param"><span className="pypsa-key">{k}</span><span className="pypsa-val">{v === null ? '(per instance)' : typeof v === 'number' ? v.toFixed(6) : String(v)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Fields */}
            <div className="def-section">
              <div className="def-section-header"><h3>Instance Fields</h3><button className="btn-sm-accent" onClick={addField}>+ Add Field</button></div>
              <div className="def-field-list">
                {(editDef.fields || []).map(field => (
                  <div key={field.id} className="def-field-row">
                    <div className="def-field-inputs">
                      <input className="field-name mono" placeholder="field_name" value={field.name} onChange={e => updateField(field.id, 'name', e.target.value)} />
                      <input className="field-label" placeholder="Label" value={field.label} onChange={e => updateField(field.id, 'label', e.target.value)} />
                      <select className="field-type" value={field.type} onChange={e => updateField(field.id, 'type', e.target.value)}>{FIELD_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                      <input className="field-default" placeholder={field.type === 'select' ? 'opt1, opt2, ...' : 'Default'} value={field.type === 'select' ? (field.options || []).join(', ') : field.defaultValue} onChange={e => { if (field.type === 'select') updateField(field.id, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean)); else updateField(field.id, 'defaultValue', e.target.value); }} />
                    </div>
                    <button className="btn-remove-field" onClick={() => removeField(field.id)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Appearance */}
          <div className="def-editor-right">
            <div className="def-section">
              <h3>Appearance</h3>
              <div className="def-form-row">
                <div className="def-form-group">
                  <label>Line Color</label>
                  <div className="color-input-wrap"><input type="color" value={editDef.color || catInfo.color} onChange={e => updateEdit('color', e.target.value)} className="color-picker" /><input type="text" value={editDef.color || ''} onChange={e => updateEdit('color', e.target.value)} className="color-hex mono" /></div>
                </div>
              </div>
              <div className="def-form-row">
                <div className="def-form-group">
                  <label>Line Style</label>
                  <select value={editDef.lineStyle || 'solid'} onChange={e => updateEdit('lineStyle', e.target.value)}>
                    <option value="solid">Solid (overhead)</option>
                    <option value="dashed">Dashed (underground/buried)</option>
                    <option value="dotted">Dotted</option>
                  </select>
                </div>
              </div>
              <div className="def-form-row">
                <div className="def-form-group">
                  <label>Canvas Line Width (px)</label>
                  <input type="number" min={1} max={8} value={editDef.lineWidth || 2} onChange={e => updateEdit('lineWidth', parseInt(e.target.value) || 2)} />
                  <span className="field-unit-inline">{'LV=1, 11kV=2, 33kV=3, 132kV=4, 400kV=5'}</span>
                </div>
              </div>
            </div>

            {/* Temperature-dependent resistance (if from catalog) */}
            {editDef.dc_resistance_20c && (
              <div className="def-section">
                <h3>Temperature-Dependent Resistance</h3>
                <div className="conductor-props-grid">
                  <PropRow label="DC Resistance (20°C)" value={editDef.dc_resistance_20c} unit={'\u03A9/km'} />
                  <PropRow label="AC Resistance (50°C)" value={editDef.ac_resistance_50c} unit={'\u03A9/km'} />
                  <PropRow label="AC Resistance (75°C)" value={editDef.ac_resistance_75c} unit={'\u03A9/km'} highlight />
                  {editDef.ac_resistance_90c && <PropRow label="AC Resistance (90°C)" value={editDef.ac_resistance_90c} unit={'\u03A9/km'} />}
                  <PropRow label="Max Operating Temp" value={editDef.max_temp_c} unit={'\u00B0C'} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
                  R used for PyPSA: AC at 75{'\u00B0C'} = {editDef.r_ohm_per_km} {'\u03A9/km'}. Loss = I{'\u00B2'}R.
                </div>
              </div>
            )}
            <div className="def-section">
              <h3>Preview</h3>
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <LinePreview def={editDef} catColor={catInfo.color} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== CATEGORY LIST ==========
  if (activeCategory) {
    const catInfo = LINE_CATEGORIES.find(c => c.id === activeCategory);
    const catDefs = defsByCategory[activeCategory] || [];

    return (
      <div className="def-list-view">
        <div className="def-list-header">
          <div>
            <h1 style={{ color: catInfo.color }}>{catInfo.icon} {catInfo.label}</h1>
            <p className="def-list-subtitle">{catInfo.description}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setActiveCategory(null)}>{'← Back to Overview'}</button>
            {activeCategory === 'electric' && (
              <>
                <button className="btn-accent" onClick={() => setShowCatalog('select')}>+ Add Line from Catalog</button>
                <button className="btn-ghost" onClick={() => setShowCatalog('browse')}>{'\u26A1'} Browse Catalog</button>
              </>
            )}
          </div>
        </div>

        {catDefs.length === 0 ? (
          <div className="node-hub-empty">No {catInfo.label.toLowerCase()} defined yet.</div>
        ) : (
          <div style={{ marginTop: 12, padding: '0 16px' }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 70px 90px 100px 90px 80px 160px',
              gap: 8,
              padding: '6px 12px',
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#94a3b8',
              borderBottom: '1px solid #334155',
            }}>
              <span>Type</span>
              <span>Name / Conductor</span>
              <span>Voltage</span>
              <span>R (Ω/km)</span>
              <span>X (Ω/km)</span>
              <span>S max</span>
              <span>I rated</span>
              <span></span>
            </div>
            {/* Rows */}
            {catDefs.map(def => {
              const isOH = def.lineType === 'overhead';
              const lineColor = def.color || catInfo.color;
              return (
                <div key={def.id}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '60px 1fr 70px 90px 100px 90px 80px 160px',
                      gap: 8,
                      padding: '8px 12px',
                      fontSize: 12,
                      color: '#e2e8f0',
                      borderBottom: '1px solid #1e293b',
                      borderLeft: `3px solid ${lineColor}`,
                      alignItems: 'center',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#1e2a4a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Type badge */}
                    <span style={{
                      fontSize: 9,
                      textTransform: 'uppercase',
                      color: lineColor,
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      {isOH ? 'OH' : 'UG'}
                    </span>
                    {/* Name */}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{def.name}</div>
                      {def.conductor && (
                        <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
                          {def.material} {def.cross_section_mm2}mm²
                        </div>
                      )}
                    </div>
                    {/* Voltage */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                      {def.voltage_kv ? `${def.voltage_kv} kV` : '—'}
                    </span>
                    {/* R */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                      {def.r_ohm_per_km != null ? def.r_ohm_per_km : '—'}
                    </span>
                    {/* X */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                      {def.x_ohm_per_km != null ? def.x_ohm_per_km : '—'}
                    </span>
                    {/* S max */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                      {def.s_max_mva ? `${def.s_max_mva} MVA` : '—'}
                    </span>
                    {/* I rated */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                      {def.i_thermal_a ? `${def.i_thermal_a} A` : '—'}
                    </span>
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button className="btn-card" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => startEdit(def)}>Edit</button>
                      {def.category === 'electric' && (
                        <button className="btn-card" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setShowPyPSA(showPyPSA === def.id ? null : def.id)}>PyPSA</button>
                      )}
                      <button className="btn-card danger" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => handleDelete(def.id)}>Del</button>
                    </div>
                  </div>
                  {showPyPSA === def.id && (
                    <div className="pypsa-box" style={{ margin: '0 0 4px 3px', borderLeft: `3px solid ${lineColor}` }}>
                      <div className="pypsa-params">
                        {Object.entries(lineToPyPSA(def)).map(([k, v]) => (
                          <div key={k} className="pypsa-param"><span className="pypsa-key">{k}</span><span className="pypsa-val">{v === null ? '(per instance)' : typeof v === 'number' ? v.toFixed(6) : String(v)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ========== HUB VIEW ==========
  if (loading) return <div className="def-list-view"><div className="def-list-header"><div><h1>Line Definitions</h1><p className="def-list-subtitle">Loading...</p></div></div></div>;

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>Line Definitions</h1>
          <p className="def-list-subtitle">Define connections between nodes: electric lines, gas and liquid pipelines.</p>
        </div>
      </div>

      <div className="node-hub-grid">
        {LINE_CATEGORIES.map(cat => {
          const catDefs = defsByCategory[cat.id] || [];
          const ohCount = catDefs.filter(d => d.lineType === 'overhead').length;
          const ugCount = catDefs.filter(d => d.lineType === 'underground').length;

          return (
            <div key={cat.id} className="node-hub-card" style={{ borderTopColor: cat.color }}>
              <div className="node-hub-card-header">
                <span className="node-hub-icon" style={{ background: cat.color }}>{cat.icon}</span>
                <div>
                  <div className="node-hub-card-title" style={{ color: cat.color }}>{cat.label}</div>
                  <div className="node-hub-card-desc">{cat.description}</div>
                </div>
              </div>

              <div className="node-hub-stats">
                <div className="node-hub-stat">
                  <span className="node-hub-stat-value" style={{ color: cat.color }}>{catDefs.length}</span>
                  <span className="node-hub-stat-label">Total</span>
                </div>
                {cat.id === 'electric' && catDefs.length > 0 && (
                  <>
                    <div className="node-hub-stat"><span className="node-hub-stat-value">{ohCount}</span><span className="node-hub-stat-label">Overhead</span></div>
                    <div className="node-hub-stat"><span className="node-hub-stat-value">{ugCount}</span><span className="node-hub-stat-label">Underground</span></div>
                  </>
                )}
                {(cat.id !== 'electric' || catDefs.length === 0) && (
                  <div className="node-hub-stat"><span className="node-hub-stat-value" style={{ color: '#8a9a95' }}>{'—'}</span><span className="node-hub-stat-label">Ready to define</span></div>
                )}
              </div>

              <div className="node-hub-list">
                {catDefs.length === 0 ? (
                  <div className="node-hub-empty-item">No lines defined yet</div>
                ) : (
                  catDefs.slice(0, 6).map(def => (
                    <div key={def.id} className="node-hub-list-item" style={{ borderLeftColor: def.color || cat.borderColor }}>
                      <span className="node-hub-list-name">{def.name}</span>
                      {def.voltage_kv && <span className="node-hub-list-meta">{def.voltage_kv} kV</span>}
                    </div>
                  ))
                )}
                {catDefs.length > 6 && <div className="node-hub-more">+{catDefs.length - 6} more</div>}
              </div>

              <button className="node-hub-open-btn" style={{ background: cat.color, color: '#1a1a2e' }} onClick={() => setActiveCategory(cat.id)}>
                {'Open ' + cat.label + ' →'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* Property row helper */
const PropRow = ({ label, value, unit, highlight }) => (
  <div className={`conductor-prop ${highlight ? 'highlight' : ''}`}>
    <span className="conductor-prop-label">{label}</span>
    <span className="conductor-prop-value">
      {value != null ? value : '—'}
      {unit && value != null && <span className="conductor-prop-unit"> {unit}</span>}
    </span>
  </div>
);

export default LineDefinitionsView;
